import { Link, useLocation, useNavigate } from 'react-router';
import { Phone, Menu, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useFirestoreDoc } from '../hooks/useDashboardData';
import { COLLECTIONS } from '../../lib/firebase';

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: settings } = useFirestoreDoc<any>(COLLECTIONS.SITE_SETTINGS, 'general');
  const contactNumber = settings?.contactNumber || '+91 91409 81723';

  const handleLogoClick = (e: React.MouseEvent) => {
    // Increment clicks
    const newCount = logoClicks + 1;
    
    if (newCount >= 5) {
      e.preventDefault();
      setLogoClicks(0);
      navigate('/admin-login');
      return;
    }

    setLogoClicks(newCount);

    // Reset counter after 3 seconds of inactivity
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => {
      setLogoClicks(0);
    }, 3000);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/properties', label: 'Properties' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 active:scale-95 transition-transform"
            onClick={handleLogoClick}
          >
            <img 
              src="/logo.png" 
              alt="Post Property Mumbai Logo" 
              className="h-20 w-auto"
            />
          </Link>


          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-label={link.label}
                aria-current={isActive(link.path) ? 'page' : undefined}
                className={`transition-colors ${
                  isActive(link.path)
                    ? 'font-semibold'
                    : 'hover:opacity-80'
                }`}
                style={{
                  color: isActive(link.path) ? 'var(--brand-blue)' : '#6b7280'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${contactNumber}`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all hover:shadow-md"
              style={{
                borderColor: 'var(--brand-blue)',
                color: 'var(--brand-blue)'
              }}
            >
              <Phone className="w-4 h-4" />
              <span>Call Us</span>
            </a>
            <Link
              to="/properties"
              className="px-6 py-2 rounded-lg text-white transition-all hover:shadow-lg hover:scale-105"
              style={{ backgroundColor: 'var(--brand-pink)' }}
            >
              View Properties
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              className="inline-flex items-center justify-center p-2.5 rounded-xl text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none transition-colors"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
            >
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="md:hidden border-b border-gray-100 bg-white overflow-hidden shadow-lg"
          >
            <nav className="px-4 pt-2 pb-6 flex flex-col gap-3" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  aria-label={link.label}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`py-2.5 px-4 rounded-xl text-base font-semibold transition-all ${
                    isActive(link.path)
                      ? 'bg-gray-50'
                      : 'hover:bg-gray-50/80 text-gray-600 hover:text-gray-900'
                  }`}
                  style={{
                    color: isActive(link.path) ? 'var(--brand-blue)' : '#4b5563'
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <hr className="border-gray-100 my-2" />
              <div className="flex flex-col gap-3 px-2">
                <a
                  href={`tel:${contactNumber}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-base font-semibold transition-all hover:bg-gray-50 active:scale-95"
                  style={{
                    borderColor: 'var(--brand-blue)',
                    color: 'var(--brand-blue)'
                  }}
                >
                  <Phone className="w-4 h-4" />
                  <span>Call Us</span>
                </a>
                <Link
                  to="/properties"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center py-3 rounded-xl text-white text-base font-semibold transition-all hover:opacity-90 hover:shadow-md active:scale-95"
                  style={{ backgroundColor: 'var(--brand-pink)' }}
                >
                  View Properties
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Login Shortcut: 5 clicks on logo */}
    </header>
  );
}
